import { useCallback, useMemo } from 'react';
import {
  EventImageConsentEnum,
  toEventImageConsent,
} from '@clickersphere/core/enums/EventImageConsentEnum';
import { useEvents } from './useEvents';

export function useEventImageConsent() {
  const { events } = useEvents();

  const consentByEventId = useMemo(() => {
    const entries = (events ?? []).map(
      (event) => [event.id, toEventImageConsent(event.imageConsent)] as const,
    );

    return new Map<string, EventImageConsentEnum>(entries);
  }, [events]);

  const leadIdsWithDeniedConsent = useMemo(() => {
    const deniedLeadIds = (events ?? [])
      .filter(
        (event) =>
          toEventImageConsent(event.imageConsent) ===
          EventImageConsentEnum.DENIED,
      )
      .map((event) => event.leadId);

    return new Set<string>(deniedLeadIds);
  }, [events]);

  const getImageConsent = useCallback(
    (eventId?: string | null): EventImageConsentEnum | undefined => {
      if (!eventId) return undefined;
      return consentByEventId.get(eventId);
    },
    [consentByEventId],
  );

  const hasLeadDeniedImageConsent = useCallback(
    (leadId?: string | null): boolean => {
      if (!leadId) return false;
      return leadIdsWithDeniedConsent.has(leadId);
    },
    [leadIdsWithDeniedConsent],
  );

  return { getImageConsent, hasLeadDeniedImageConsent };
}
